import { useMemo, useRef, useState } from 'react'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import { PageHeader } from '@/lib/page-header'
import { useLocalStorage } from '@/lib/use-local-storage'
import * as topojson from 'topojson-client'
import * as d3 from 'd3'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
} from 'recharts'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { format } from 'date-fns'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import { usePrimoData, getPrice, formatEUR, COUNTRY_ISO_NUMERIC, PRICE_LEVELS, type PrimoRecord } from '@/lib/primodata'
import { COUNTRY_NAMES, COUNTRY_ALPHA2, getFlagEmoji } from '@/lib/country-names'
import { usePriceFilters, useFilteredData } from '@/lib/price-filters'

export const Route = createFileRoute('/_shell/priser')({
  validateSearch: (s: Record<string, unknown>) => ({
    view: (s.view as string | undefined) ?? 'chart',
  }),
  component: PriserPage,
})

// ─── Shared ───────────────────────────────────────────────────────────────────

const CHART_COLORS = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
]

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    'Price change': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    'New pack': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    Discontinued: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'No change': 'bg-muted text-muted-foreground',
  }
  const labels: Record<string, string> = {
    'Price change': 'Prisændring', 'New pack': 'Ny pakke',
    Discontinued: 'Udgået', 'No change': 'Ingen ændring',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg[status] ?? 'bg-muted'}`}>
      {labels[status] ?? status}
    </span>
  )
}

// ─── Bar chart ────────────────────────────────────────────────────────────────

function BarChartView({ data }: { data: PrimoRecord[] }) {
  const { priceLevel } = usePriceFilters()
  const priceLevelLabel = PRICE_LEVELS.find(l => l.value === priceLevel)?.label ?? priceLevel

  const drugs = useMemo(
    () => [...new Set(data.map(r => r['Global Product Name']))].sort(),
    [data]
  )

  // One row per country; each drug is a column (max price per drug per country)
  const chartData = useMemo(() => {
    const byCountry = new Map<string, Record<string, number | string>>()
    for (const r of data) {
      const p = getPrice(r, priceLevel)
      if (!p) continue
      const entry = byCountry.get(r.Country) ?? { country: r.Country }
      const prev = entry[r['Global Product Name']] as number | undefined
      entry[r['Global Product Name']] = Math.max(prev ?? 0, p)
      byCountry.set(r.Country, entry)
    }
    return [...byCountry.values()]
      .sort((a, b) => {
        const maxA = drugs.reduce((m, d) => Math.max(m, (a[d] as number) ?? 0), 0)
        const maxB = drugs.reduce((m, d) => Math.max(m, (b[d] as number) ?? 0), 0)
        return maxB - maxA
      })
      .slice(0, 20)
  }, [data, priceLevel, drugs])

  const formatY = (v: number) =>
    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` :
    v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : String(v)

  const chartConfig = {} satisfies ChartConfig

  if (!chartData.length) return (
    <p className="py-12 text-center text-muted-foreground">Ingen data for valgte filtre.</p>
  )

  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <ChartContainer config={chartConfig} className="h-[440px]">
        <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 64, left: 16 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="country"
            tick={{ fontSize: 11 }}
            tickLine={false}
            angle={-40}
            textAnchor="end"
            height={72}
          />
          <YAxis
            tickFormatter={formatY}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              return (
                <div className="rounded-lg border bg-background p-3 shadow-md text-xs space-y-1.5 min-w-[180px]">
                  <p className="font-semibold">{label}</p>
                  {payload.filter(p => p.value).map(p => (
                    <div key={String(p.dataKey)} className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: p.fill }} />
                        {String(p.dataKey)}
                      </span>
                      <span className="font-mono font-medium">{formatEUR(p.value as number)}</span>
                    </div>
                  ))}
                </div>
              )
            }}
          />
          {drugs.length <= 8 && (
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              iconType="circle"
              iconSize={8}
            />
          )}
          {drugs.map((drug, i) => (
            <Bar
              key={drug}
              dataKey={drug}
              fill={CHART_COLORS[i % CHART_COLORS.length]}
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
            />
          ))}
        </BarChart>
      </ChartContainer>
      {data.length > 20 && (
        <p className="text-xs text-muted-foreground text-right">
          Viser top 20 lande · {data.length} rækker i alt · prisniveau: {priceLevelLabel}
        </p>
      )}
    </div>
  )
}

// ─── Table ────────────────────────────────────────────────────────────────────

interface TableRow {
  drug: string; pkg: string; country: string
  price: number | null; localPrice: number | null; localCurrency: string
  status: string; date: string | null; cot: number | null
}

function TableView({ data }: { data: PrimoRecord[] }) {
  const { priceLevel } = usePriceFilters()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'price', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')
  const priceLevelLabel = PRICE_LEVELS.find(l => l.value === priceLevel)?.label ?? priceLevel

  const tableData = useMemo<TableRow[]>(() =>
    data.map(r => ({
      drug: r['Global Product Name'], pkg: r.Packages, country: r.Country,
      price: getPrice(r, priceLevel), localPrice: r['Local Price'],
      localCurrency: r['Local Currency'] ?? '', status: r.Status,
      date: r['Price Start Date'], cot: r.COT,
    })),
    [data, priceLevel]
  )

  const SortHeader = ({ column, label }: { column: any; label: string }) => (
    <Button variant="ghost" size="sm" onClick={() => column.toggleSorting()} className="h-7 gap-1 -ml-2 font-medium">
      {label}
      {column.getIsSorted() === 'asc' ? <ArrowUp className="h-3 w-3" /> :
       column.getIsSorted() === 'desc' ? <ArrowDown className="h-3 w-3" /> :
       <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
    </Button>
  )

  const columns = useMemo<ColumnDef<TableRow>[]>(() => [
    { accessorKey: 'drug', header: ({ column }) => <SortHeader column={column} label="Lægemiddel" /> },
    { accessorKey: 'pkg', header: 'Pakke', cell: ({ getValue }) => (
        <span className="text-xs text-muted-foreground max-w-[200px] block truncate" title={getValue() as string}>{getValue() as string}</span>
    )},
    { accessorKey: 'country', header: ({ column }) => <SortHeader column={column} label="Land" /> },
    { accessorKey: 'price', header: ({ column }) => <SortHeader column={column} label={priceLevelLabel} />,
      cell: ({ getValue }) => <span className="font-mono text-right block">{formatEUR(getValue() as number | null)}</span> },
    { accessorKey: 'localPrice', header: 'Lokal pris', cell: ({ row }) => {
        const lp = row.original.localPrice, lc = row.original.localCurrency
        if (!lp || !lc) return <span className="text-muted-foreground">—</span>
        return <span className="font-mono text-right block text-xs">{new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(lp)} {lc}</span>
    }},
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <StatusBadge status={getValue() as string} /> },
    { accessorKey: 'date', header: ({ column }) => <SortHeader column={column} label="Gyldig fra" />,
      cell: ({ getValue }) => { const v = getValue() as string | null; return v ? format(new Date(v), 'dd.MM.yyyy') : <span className="text-muted-foreground">—</span> } },
  ], [priceLevelLabel])

  const table = useReactTable({
    data: tableData, columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting, onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(), getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 30 } },
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input placeholder="Søg i alle kolonner…" value={globalFilter}
          onChange={e => setGlobalFilter(e.target.value)} className="h-9 max-w-sm text-sm" />
        <span className="text-xs text-muted-foreground ml-auto">{table.getFilteredRowModel().rows.length} rækker</span>
      </div>
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(hg => (
              <TableRow key={hg.id} className="bg-muted/50 hover:bg-muted/50">
                {hg.headers.map(h => <TableHead key={h.id} className="py-2">{flexRender(h.column.columnDef.header, h.getContext())}</TableHead>)}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow key={row.id} className="hover:bg-muted/30">
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id} className="py-2 text-sm">{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
            {!table.getRowModel().rows.length && (
              <TableRow><TableCell colSpan={columns.length} className="py-12 text-center text-muted-foreground">Ingen data.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Forrige</Button>
          <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Næste</Button>
        </div>
        <span className="text-xs text-muted-foreground">Side {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</span>
      </div>
    </div>
  )
}

// ─── World map ────────────────────────────────────────────────────────────────

const MAP_W = 960
const MAP_H = 460

const COLOR_SCHEMES = [
  { id: 'ylord',   label: 'Warm',       interpolator: d3.interpolateYlOrRd          },
  { id: 'viridis', label: 'Viridis',    interpolator: d3.interpolateViridis         },
  { id: 'plasma',  label: 'Plasma',     interpolator: d3.interpolatePlasma          },
  { id: 'gtr',     label: 'Green→Red',  interpolator: (t: number) => d3.interpolateRdYlGn(1 - t) },
] as const
type SchemeId = typeof COLOR_SCHEMES[number]['id']

interface CountryPrices {
  exFactory: number | null
  hospital:  number | null
  pharmacy:  number | null
  publicVat: number | null
}

interface TooltipState {
  x: number; y: number
  containerW: number; containerH: number
  name: string
  flag: string
  prices: CountryPrices | null
}

function MapTooltip({ tip }: { tip: TooltipState }) {
  const { x, y, containerW, containerH, name, prices } = tip
  const estimatedH = prices ? 120 : 64
  const left = Math.min(x + 14, containerW - 220)
  const top = y + 14 + estimatedH > containerH ? y - estimatedH - 8 : y + 14

  return (
    <div
      className="pointer-events-none absolute z-50 rounded-lg border bg-background shadow-md p-3 text-xs min-w-[200px]"
      style={{ left, top }}
    >
      <p className="font-semibold text-sm mb-1.5">
        {tip.flag && <span className="mr-1.5">{tip.flag}</span>}{name}
      </p>
      {!prices ? (
        <p className="italic text-muted-foreground">Ikke tilgængeligt i dette land</p>
      ) : (
        <div className="space-y-1">
          {([
            ['Ex-factory',          prices.exFactory],
            ['Hospital',            prices.hospital],
            ['Apotek',              prices.pharmacy],
            ['Offentlig (inkl. moms)', prices.publicVat],
          ] as [string, number | null][]).map(([label, val]) => (
            <div key={label} className="flex items-baseline justify-between gap-4">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono font-medium">{formatEUR(val)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function WorldMapView({ data }: { data: PrimoRecord[] }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const [hoveredIso, setHoveredIso] = useState<string | null>(null)
  const [scheme, setScheme] = useLocalStorage<SchemeId>('map.colorScheme', 'ylord')

  const { data: worldAtlas, isLoading } = useQuery({
    queryKey: ['world-atlas'],
    queryFn: () =>
      fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json').then(r => r.json()),
    staleTime: Infinity,
  })

  // GeoJSON features (memoised — only depends on atlas)
  const features = useMemo(() => {
    if (!worldAtlas) return []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fc = topojson.feature(worldAtlas as any, (worldAtlas as any).objects.countries) as any
    return fc.features as Array<{ id: number | string; type: string; geometry: unknown; properties: unknown }>
  }, [worldAtlas])

  // Aggregate all 4 price types per ISO code
  const pricesByIso = useMemo(() => {
    type Acc = { ef: number; hp: number; ph: number; pv: number; n: number }
    const acc = new Map<string, Acc>()
    for (const r of data) {
      const iso = COUNTRY_ISO_NUMERIC[r.Country]
      if (!iso) continue
      const a = acc.get(iso) ?? { ef: 0, hp: 0, ph: 0, pv: 0, n: 0 }
      const ef = getPrice(r, 'Ex Factory Price')
      const hp = getPrice(r, 'Hospital Price')
      const ph = getPrice(r, 'Pharmacy Price')
      const pv = getPrice(r, 'Public Price with VAT')
      if (ef) a.ef += ef
      if (hp) a.hp += hp
      if (ph) a.ph += ph
      if (pv) a.pv += pv
      a.n += 1
      acc.set(iso, a)
    }
    const out = new Map<string, CountryPrices>()
    for (const [iso, a] of acc) {
      out.set(iso, {
        exFactory: a.ef > 0 ? a.ef / a.n : null,
        hospital:  a.hp > 0 ? a.hp / a.n : null,
        pharmacy:  a.ph > 0 ? a.ph / a.n : null,
        publicVat: a.pv > 0 ? a.pv / a.n : null,
      })
    }
    return out
  }, [data])

  // Color scale driven by ex-factory price + selected scheme
  const { colorScale, maxPrice } = useMemo(() => {
    const vals = [...pricesByIso.values()]
      .map(p => p.exFactory).filter((v): v is number => v != null)
    const maxPrice = Math.max(...vals, 1)
    const interpolator = COLOR_SCHEMES.find(s => s.id === scheme)!.interpolator
    const colorScale = d3.scaleSequential(interpolator).domain([0, maxPrice])
    return { colorScale, maxPrice }
  }, [pricesByIso, scheme])

  const isoFromFeature = (id: number | string) =>
    String(typeof id === 'number' ? id : Number(id)).padStart(3, '0')

  // Fast ISO → feature lookup for the hover overlay
  const featureByIso = useMemo(() => {
    const map = new Map<string, typeof features[0]>()
    for (const f of features) map.set(isoFromFeature(f.id), f)
    return map
  }, [features])

  // D3 path generator at fixed internal resolution (SVG viewBox handles scaling)
  const pathFn = useMemo(() => {
    if (!features.length) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const proj = d3.geoEqualEarth().fitSize([MAP_W, MAP_H], { type: 'FeatureCollection', features } as any)
    // Note: alternative d3.geoNaturalEarth1
    // Note2: pathGenerator = projection(proj)
    // svg.append('path')
    // .attr('class', 'sphere')
    // .attr('d', pathGenerator({type: 'Sphere'}));
    return d3.geoPath().projection(proj)
  }, [features])

  // Gradient stops for legend
  const gradientStops = useMemo(
    () => d3.range(0, 11).map(i => colorScale(maxPrice * i / 10)),
    [colorScale, maxPrice]
  )

  const countryName = (iso: string) =>
    COUNTRY_NAMES[iso] ??
    Object.entries(COUNTRY_ISO_NUMERIC).find(([, v]) => v === iso)?.[0] ??
    `(${iso})`

  if (isLoading) return (
    <p className="py-12 text-center text-muted-foreground">Indlæser verdenskort…</p>
  )

  return (
    <div ref={containerRef} className="relative w-full rounded-lg border bg-card overflow-hidden">
      {/* Map — viewBox makes it inherently 100% wide */}
      <svg
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        style={{ display: 'block', width: '100%', height: 'auto' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {pathFn && features.map(feature => {
          const iso = isoFromFeature(feature.id)
          const prices = pricesByIso.get(iso) ?? null
          const fill = prices?.exFactory != null ? colorScale(prices.exFactory) : '#d4d4d8'
          return (
            <path
              key={iso}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              d={pathFn(feature as any) ?? ''}
              fill={fill}
              stroke="white"
              strokeWidth={0.4}
              style={{ cursor: 'default' }}
              onMouseMove={e => {
                const rect = containerRef.current!.getBoundingClientRect()
                setHoveredIso(iso)
                setTooltip({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                  containerW: rect.width,
                  containerH: rect.height,
                  name: countryName(iso),
                  flag: getFlagEmoji(COUNTRY_ALPHA2[iso] ?? ''),
                  prices,
                })
              }}
              onMouseLeave={() => { setHoveredIso(null); setTooltip(null) }}
            />
          )
        })}

        {/* Hover outline — rendered on top so it's never obscured */}
        {hoveredIso && pathFn && featureByIso.get(hoveredIso) && (
          <path
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            d={pathFn(featureByIso.get(hoveredIso) as any) ?? ''}
            fill="none"
            stroke="rgba(0,0,0,0.75)"
            strokeWidth={1.5}
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </svg>

      {/* Color legend + scheme toggle */}
      <div className="absolute bottom-3 right-4 flex flex-col items-end gap-1.5">
        {/* Scheme toggle */}
        <div className="flex rounded-md overflow-hidden border bg-background/80 backdrop-blur-sm shadow-sm text-[11px]">
          {COLOR_SCHEMES.map(s => (
            <button
              key={s.id}
              onClick={() => setScheme(s.id)}
              className={`px-2 py-1 transition-colors ${
                scheme === s.id
                  ? 'bg-foreground text-background font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {/* Gradient bar */}
        <div className="flex items-center gap-2 rounded-md bg-background/80 backdrop-blur-sm px-2.5 py-1.5 shadow-sm border">
          <span className="text-[11px] text-muted-foreground font-mono">0</span>
          <div
            className="w-32 h-2.5 rounded-sm"
            style={{ background: `linear-gradient(to right, ${gradientStops.join(', ')})` }}
          />
          <span className="text-[11px] text-muted-foreground font-mono">
            {maxPrice >= 1_000_000
              ? `${(maxPrice / 1_000_000).toFixed(1)}M €`
              : `${(maxPrice / 1_000).toFixed(0)}K €`}
          </span>
        </div>
      </div>

      {tooltip && <MapTooltip tip={tooltip} />}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PriserPage() {
  const search = useSearch({ from: '/_shell/priser' })
  const { data, isLoading } = usePrimoData()
  const filteredData = useFilteredData(data)
  const defaultTab = search.view === 'map' ? 'map' : 'chart'

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Vis priser" />
      {isLoading ? (
        <p className="text-muted-foreground">Indlæser data…</p>
      ) : (
        <Tabs defaultValue={defaultTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="chart">Søjlediagram</TabsTrigger>
            <TabsTrigger value="table">Tabel</TabsTrigger>
            <TabsTrigger value="map">Verdenskort</TabsTrigger>
          </TabsList>
          <TabsContent value="chart"><BarChartView data={filteredData} /></TabsContent>
          <TabsContent value="table"><TableView data={filteredData} /></TabsContent>
          <TabsContent value="map"><WorldMapView data={filteredData} /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}
